import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { MAX_PAGE_SIZE, FIRST_PAGE } from '../../../Consts/backoffice.const';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import {
  LicenceFreePoolResponse,
  LicenceResponse,
  LicenceShowResponse,
} from '../../../Interfaces/api/backoffice/licence.interface';
import { TenantUserListResponse } from '../../../Interfaces/api/backoffice/user.interface';
import { Licence } from '../../../Models/backoffice/licence.model';
import { TenantUser } from '../../../Models/backoffice/tenant-user.model';

@Injectable({
  providedIn: 'root',
})
export class LicencePortfolioService {
  private readonly _apiService = inject(ApiService);

  freePoolRequest(
    organizationId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<LicenceFreePoolResponse>> {
    return this._apiService.get<LaravelDefaultResponse<LicenceFreePoolResponse>>(
      endpoints.backoffice.organizationLicencesFreePool(organizationId),
      { skipLoading: true, skipError: true },
    );
  }

  organizationUsersRequest(
    organizationId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<TenantUserListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<TenantUserListResponse>>(
      endpoints.backoffice.users,
      {
        params: {
          organization_id: organizationId,
          page: FIRST_PAGE,
          pageSize: MAX_PAGE_SIZE,
        },
        skipLoading: true,
        skipError: true,
      },
    );
  }

  assign(
    organizationId: string,
    licenceId: string,
    userId: string,
    reason: string,
  ): Observable<Licence | null> {
    return this._licenceMutation(
      endpoints.backoffice.organizationLicenceAssign(organizationId, licenceId),
      { user_id: userId, reason },
    );
  }

  release(organizationId: string, licenceId: string, reason: string): Observable<Licence | null> {
    return this._licenceMutation(
      endpoints.backoffice.organizationLicenceRelease(organizationId, licenceId),
      { reason },
    );
  }

  toFreePool(
    response: LaravelDefaultResponse<LicenceFreePoolResponse> | undefined,
  ): LicenceFreePoolResponse | null {
    return response?.data ?? null;
  }

  toUsers(response: LaravelDefaultResponse<TenantUserListResponse> | undefined): TenantUser[] {
    return (response?.data?.users ?? []).map((row) => new TenantUser(row));
  }

  private _licenceMutation(
    resource: string,
    body: Record<string, unknown>,
  ): Observable<Licence | null> {
    const request = this._apiService.post<LaravelDefaultResponse<LicenceShowResponse>>(resource, {
      body,
      skipError: true,
    });

    return this._apiService
      .run<LaravelDefaultResponse<LicenceShowResponse>>(request)
      .pipe(map((response) => this._toLicence(response.data.licence)));
  }

  private _toLicence(row: LicenceResponse | null | undefined): Licence | null {
    return row ? new Licence(row) : null;
  }
}
